import React, { useEffect, useState } from 'react';
import { Modal, Button, Spinner, Checkbox, Label } from 'flowbite-react';
import { t } from 'i18next';
import { EditRadioGroup } from '../Shared/EditRadioGroup';
import { EditTextInput } from '../Shared/EditTextInput';
import { IVersion } from '../../common/interfaces/Version.interface';
import { OsType } from '../../common/enums/Os';
import {
  useAddVersionMutation,
  useUpdateVersionMutation,
} from '../../redux/api/versionManagment';
import { IMultiLanguageValue } from '../../common/interfaces/MultiLanguageValue.interface';
import { MultiLangVersionTextArea } from './MultiLangVersionTextArea';
import { languageOptions } from '../../common/constants/languageOptions';
interface Props {
  isOpen: boolean;
  onClose: () => void;
  versionToEdit?: IVersion | null;
}

const AddVersionModal: React.FC<Props> = ({
  isOpen,
  onClose,
  versionToEdit,
}) => {
  const [formData, setFormData] = useState<IVersion>({
    id: '',
    appVersion: '',
    osType: OsType.Android,
    forceUpdateRequired: false,
    message: [
      { language: 'en', value: '' },
      { language: 'ar', value: '' },
    ],
    link: '',
  });
  const [addVersion, { isLoading }] = useAddVersionMutation();
  const [updateVersion, { isLoading: isUpdating }] = useUpdateVersionMutation();

  const [selectedLanguage, setSelectedLanguage] = useState(languageOptions[0]);

  const messageForCurrentLang = formData.message.find(
    (m) => m.language === selectedLanguage.languageCode
  );
  const disabledButton = !(
    formData.appVersion.trim() &&
    messageForCurrentLang?.value.trim() &&
    formData.link.trim()
  );

  useEffect(() => {
    if (isOpen) {
      if (versionToEdit) {
        setFormData(versionToEdit);
      } else {
        setFormData({
          id: '',
          appVersion: '',
          osType: OsType.Android,
          forceUpdateRequired: false,
          message: [
            { language: 'en', value: '' },
            { language: 'ar', value: '' },
          ],
          link: '',
        });
      }
    }
  }, [isOpen, versionToEdit]);

  const updateVersionField = (
    field: keyof IVersion,
    value: IMultiLanguageValue[]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (versionToEdit) {
        await updateVersion({
          id: versionToEdit.id,
          updatedContent: formData,
        }).unwrap();
      } else {
        await addVersion(formData).unwrap();
      }
      onClose();
    } catch (error) {
      // The toast middleware reports the failure; the modal stays open with the input.
    }
  };

  return (
    <Modal show={isOpen} size="4xl" onClose={onClose}>
      <Modal.Header className="modal-header">
        <div className="flex-1">
          {versionToEdit
            ? t('versionsManagement.editVersion')
            : t('versionsManagement.addVersion')}
        </div>
      </Modal.Header>
      <Modal.Body>
        <form id="addVersionForm" onSubmit={handleSubmit} className="space-y-4">
          {versionToEdit ? (
            <input
              type="text"
              className="w-full cursor-not-allowed rounded-lg border border-primary-50 p-2"
              value={formData.osType}
              readOnly
            />
          ) : (
            <EditRadioGroup
              label={t('versionsManagement.osType')}
              options={Object.values(OsType)}
              selectedValue={formData.osType}
              onChange={(value) =>
                setFormData({ ...formData, osType: value as OsType })
              }
              optionName="osType"
            />
          )}

          <EditTextInput
            label={t('versionsManagement.appVersion')}
            type="text"
            value={formData.appVersion}
            onChange={(value) =>
              setFormData({ ...formData, appVersion: value })
            }
            placeholder={t('versionsManagement.appVersion')}
          />

          <EditTextInput
            label={t('versionsManagement.minimumVersion')}
            type="text"
            value={formData.minimumVersion ?? ''}
            onChange={(value) =>
              setFormData({ ...formData, minimumVersion: value })
            }
            placeholder={t('versionsManagement.minimumVersion')}
          />

          <EditTextInput
            label={t('versionsManagement.link')}
            type="url"
            value={formData.link}
            onChange={(value) => setFormData({ ...formData, link: value })}
            placeholder={t('versionsManagement.link')}
          />

          <div className="flex items-center gap-2">
            <Checkbox
              id="forceUpdateRequired"
              name="forceUpdateRequired"
              checked={formData.forceUpdateRequired}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  forceUpdateRequired: e.target.checked,
                })
              }
            />
            <Label htmlFor="forceUpdateRequired">
              {t('versionsManagement.forceUpdateRequired')}
            </Label>
          </div>

          <div className="space-y-2">
            <MultiLangVersionTextArea
              t={t}
              version={formData}
              updateVersionField={updateVersionField}
              selectedLanguage={selectedLanguage}
              setSelectedLanguage={setSelectedLanguage}
            />
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex w-full items-center justify-between gap-4">
          {/* disabled if no required data */}
          <Button
            gradientMonochrome="success"
            type="submit"
            form="addVersionForm"
            disabled={isLoading || disabledButton || isUpdating}
          >
            {isLoading || isUpdating ? (
              <Spinner color="success" size="sm" />
            ) : (
              t('content.save')
            )}
          </Button>
          <Button color="gray" onClick={onClose} disabled={isLoading}>
            {t('media.close')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
export default AddVersionModal;
